"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import {
  GraduationCap,
  Sparkles,
  Mail,
  Send,
  CheckCircle2,
  Check,
  Calendar,
  ShoppingCart,
  BookOpen,
  Target,
  Brain,
  Trophy,
  Users,
  ArrowRight,
  ChevronDown,
  Clock,
  ListChecks,
  FileText,
  Shield,
  Zap,
  Gift,
  MousePointerClick,
  Rocket,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  Music2,
  Star,
  PlayCircle,
} from "lucide-react";
import type { Matura2027Social } from "@/lib/settings";
import type { SocialPlatform } from "@/lib/cms-types";
import Footer from "@/components/sections/Footer";
import Navbar from "@/components/sections/Navbar";
import BlogNewsCarousel from "@/components/blog/BlogNewsCarousel";
import PromoCountdown from "@/components/ui/PromoCountdown";
import { useCart } from "@/lib/cart";
import { formatPriceLabel } from "@/lib/course-price-label";

// Ikony + kolory marek dla sekcji social media
const SOCIAL_META: Record<
  SocialPlatform,
  { Icon: React.ComponentType<{ className?: string }>; label: string; gradient: string; glow: string }
> = {
  facebook: { Icon: Facebook, label: "Facebook", gradient: "from-blue-500 to-blue-700", glow: "bg-blue-500/25" },
  instagram: {
    Icon: Instagram,
    label: "Instagram",
    gradient: "from-fuchsia-500 via-rose-500 to-amber-500",
    glow: "bg-fuchsia-500/25",
  },
  youtube: { Icon: Youtube, label: "YouTube", gradient: "from-red-500 to-red-700", glow: "bg-red-500/25" },
  twitter: { Icon: Twitter, label: "Twitter / X", gradient: "from-slate-700 to-slate-900", glow: "bg-slate-500/25" },
  linkedin: { Icon: Linkedin, label: "LinkedIn", gradient: "from-sky-600 to-sky-800", glow: "bg-sky-500/25" },
  tiktok: { Icon: Music2, label: "TikTok", gradient: "from-slate-800 via-cyan-600 to-rose-500", glow: "bg-cyan-500/25" },
};

interface Matura2027ClientProps {
  title: string;
  socialEnabled: boolean;
  socialTitle: string;
  socialSubtitle: string;
  socials: Matura2027Social[];
  ambassadorEnabled: boolean;
  coursesEnabled: boolean;
  coursesTitle: string;
  coursesSubtitle: string;
  coursesCtaText: string;
  blogCarouselEnabled: boolean;
  freeLessonTitle?: string | null;
}

interface LandingCourseLite {
  id: string;
  slug: string;
  title: string;
  description: string;
  thumbnail: string;
  cardColor: string;
  price: number;
  originalPrice: number;
  priceLabel: string;
  landingTagline: string;
  freeLessonCount: number;
  firstFreeLessonId: string | null;
  registrationBonusLessonId: string | null;
  totalLessons: number;
  taskCount: number;
  rating: number;
  authorName: string;
  authorPhoto: string;
  promoEndsAt: string | null;
  studentCount: number;
  studentAvatars: { initials: string; image: string }[];
}

const PLAN_FEATURES = [
  {
    icon: Calendar,
    title: "Harmonogram nauki",
    desc: "Spersonalizowany kalendarz krok po kroku do egzaminu. Wiesz dokładnie, co robić każdego dnia.",
  },
  {
    icon: ListChecks,
    title: "Checklisty z lektur",
    desc: "Pełna lista lektur z opracowaniami, motywami i cytatami, których nie możesz przegapić.",
  },
  {
    icon: Target,
    title: "Strategie egzaminacyjne",
    desc: "Sprawdzone techniki rozwiązywania zadań, które oszczędzą Ci czas i nerwy w dniu matury.",
  },
  {
    icon: FileText,
    title: "Banki zadań CKE",
    desc: "Setki zadań z poprzednich matur z rozwiązaniami i komentarzami nauczycieli.",
  },
  {
    icon: Brain,
    title: "Techniki zapamiętywania",
    desc: "Metody nauki oparte na nauce kognitywnej. Naucz się więcej w krótszym czasie.",
  },
  {
    icon: Clock,
    title: "Symulacje pod czas",
    desc: "Próbne matury w warunkach egzaminacyjnych, żeby nic Cię nie zaskoczyło.",
  },
];

const HOW_IT_WORKS = [
  {
    icon: Mail,
    step: "01",
    title: "Zostaw e-mail",
    desc: "Wpisz adres i kliknij „Odbierz plan”. Bez rejestracji, bez karty.",
  },
  {
    icon: MousePointerClick,
    step: "02",
    title: "Odbierz plan w 60 s",
    desc: "PDF z harmonogramem i listą lektur trafia od razu na Twoją skrzynkę.",
  },
  {
    icon: Rocket,
    step: "03",
    title: "Zacznij się uczyć",
    desc: "Działaj krok po kroku. Co tydzień dostajesz nowe materiały i wskazówki.",
  },
];

const WHY_US = [
  {
    icon: Gift,
    title: "100% za darmo",
    desc: "Pełny plan przygotowań do matury 2027 bez żadnych opłat. Zostawiasz tylko adres e-mail.",
    gradient: "from-emerald-400 to-teal-500",
    glow: "bg-emerald-400/25",
  },
  {
    icon: Target,
    title: "Zgodne z CKE",
    desc: "Materiały oparte na aktualnych wymaganiach egzaminacyjnych. Zero zgadywania, co będzie na maturze.",
    gradient: "from-blue-400 to-indigo-500",
    glow: "bg-blue-400/25",
  },
  {
    icon: Zap,
    title: "Aktualizacje na bieżąco",
    desc: "Plan rośnie razem z Tobą — dorzucamy nowe materiały i wskazówki przez cały rok szkolny.",
    gradient: "from-amber-400 to-orange-500",
    glow: "bg-amber-400/25",
  },
  {
    icon: Shield,
    title: "Bez spamu",
    desc: "Dostajesz tylko to, co realnie pomaga w nauce. Twoje dane są u nas bezpieczne.",
    gradient: "from-fuchsia-400 to-pink-500",
    glow: "bg-fuchsia-400/25",
  },
];

const FAQ_ITEMS = [
  {
    q: "Czy plan maturalny jest naprawdę darmowy?",
    a: "Tak, w pełni. Zostawiasz tylko adres e-mail i otrzymujesz kompletny plan przygotowania do matury 2027 — bez ukrytych kosztów i bez zobowiązań.",
  },
  {
    q: "Dla kogo jest ten plan?",
    a: "Dla każdego maturzysty, niezależnie od poziomu. Plan jest spersonalizowany — dopasujesz go do swojego harmonogramu i przedmiotów.",
  },
  {
    q: "Kiedy najlepiej zacząć przygotowania?",
    a: "Najlepiej jak najwcześniej. Z planem NextSchool nawet jeśli zaczniesz później, wiesz dokładnie na czym się skupić, żeby nadrobić.",
  },
  {
    q: "Co dostanę zaraz po zapisaniu się?",
    a: "PDF z kompletnym harmonogramem, listę lektur z opracowaniami, dostęp do darmowych materiałów wideo i powiadomienia o nowościach.",
  },
];

export default function Matura2027Client({
  title,
  socialEnabled,
  socialTitle,
  socialSubtitle,
  socials,
  ambassadorEnabled,
  coursesEnabled,
  coursesTitle,
  coursesSubtitle,
  coursesCtaText,
  blogCarouselEnabled,
  freeLessonTitle,
}: Matura2027ClientProps) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [landingCourses, setLandingCourses] = useState<LandingCourseLite[]>([]);
  const { addItem, openCart, hasItem } = useCart();

  useEffect(() => {
    if (!coursesEnabled) return;
    let cancelled = false;
    fetch("/api/courses/landing")
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!cancelled) setLandingCourses(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [coursesEnabled]);

  function handleBuyCourse(course: LandingCourseLite) {
    addItem({
      id: course.id,
      slug: course.slug,
      title: course.title,
      price: course.price,
      originalPrice: course.originalPrice,
      thumbnail: course.thumbnail,
      cardColor: course.cardColor,
    });
    openCart();
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const valid = /.+@.+\..+/.test(email);
    if (!valid) {
      setError("Wpisz prawidłowy adres e-mail");
      return;
    }
    if (!acceptedPrivacy) {
      setError("Zaakceptuj politykę prywatności, aby odebrać plan.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "matura-2027-plan" }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Nie udało się zapisać");
      }
      setSubmitted(true);
      setEmail("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Wystąpił błąd. Spróbuj ponownie.");
    } finally {
      setSubmitting(false);
    }
  };

  // Materiał na wizualizację po prawej stronie hero — realne dane kursu/nauczycieli.
  const heroCourse = landingCourses[0];
  const heroTeachers = Array.from(
    new Map(
      landingCourses.filter((c) => c.authorName).map((c) => [c.authorName, c.authorPhoto])
    ).entries()
  ).slice(0, 4);

  return (
    <>
    {/* ─── Header (edytowalny z CMS — zakres „Matura 2027") — poza MotionConfig, by zachować animacje paska ─── */}
    <Navbar scope="matura2027" />
    <MotionConfig reducedMotion="always">
    <div className="relative min-h-screen bg-white text-slate-900 antialiased">
      {/* ─── Hero ─── */}
      <section
        id="plan"
        className="relative"
        style={{
          background:
            "linear-gradient(180deg, #eff6ff 0%, #faf5ff 35%, #ffffff 100%)",
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-20 sm:pb-28 lg:grid lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-16 xl:gap-24">
          <motion.div className="max-w-3xl">
            <h1 className="max-w-4xl text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
              {title.endsWith("2027") ? (
                <>
                  {title.slice(0, -4)}
                  <span className="text-blue-700">2027</span>
                </>
              ) : title}
            </h1>

            <div className="mb-8 flex flex-wrap items-center gap-3">
              <Link
                href={coursesEnabled ? "#kursy" : "/kursy-maturalne"}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-800"
              >
                <BookOpen className="h-4 w-4" />
                Zobacz kursy i ceny
              </Link>
              <Link
                href="#formularz"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300"
              >
                Odbierz bezpłatny plan nauki
              </Link>
            </div>

            {/* Signup form */}
            <div
              id="formularz"
              className="max-w-xl relative"
            >
              <h2 className="mb-3 text-lg font-bold text-slate-900">Wyślemy plan maturalny na Twój e-mail</h2>
              <AnimatePresence mode="wait">
                {submitted ? (
                  <div
                    key="success"
                    className="relative flex flex-col items-start gap-3 p-6 bg-emerald-50 border border-emerald-200 rounded-xl"
                  >
                    <CheckCircle2 className="w-8 h-8 text-emerald-700" strokeWidth={2.5} />
                    <motion.div
                      className="text-left"
                    >
                      <div className="font-extrabold text-emerald-900 text-xl mb-1">
                        Świetnie! 🎉
                      </div>
                      <p className="text-sm text-emerald-700 max-w-xs">
                        {freeLessonTitle
                          ? "Sprawdź skrzynkę — plan maturalny i link do darmowej lekcji są już w drodze."
                          : "Sprawdź skrzynkę — plan maturalny jest już w drodze."}
                      </p>
                    </motion.div>
                  </div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    {freeLessonTitle && (
                      <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-xs sm:text-sm font-semibold text-emerald-700">
                        <Gift className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>
                          Bonus: dostaniesz też link do darmowej lekcji „{freeLessonTitle}” — od razu, bez logowania i rejestracji.
                        </span>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2 p-2 bg-white rounded-xl ring-1 ring-slate-200">
                      <div className="flex-1 flex items-center gap-2 px-4">
                        <Mail className="w-5 h-5 text-slate-400 shrink-0" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="twoj@email.pl"
                          className="flex-1 py-3 bg-transparent outline-none text-sm placeholder:text-slate-400"
                          disabled={submitting}
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? (
                          <>
                            <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full" />
                            <span>Wysyłanie...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            <span>Wyślij mi plan</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Zgoda na politykę prywatności */}
                    <label className="group flex cursor-pointer select-none items-start gap-2.5 pt-1 text-left">
                      <input
                        type="checkbox"
                        checked={acceptedPrivacy}
                        onChange={(e) => {
                          setAcceptedPrivacy(e.target.checked);
                          if (e.target.checked) setError("");
                        }}
                        className="sr-only"
                        aria-label="Akceptuję politykę prywatności"
                      />
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all ${
                          acceptedPrivacy
                            ? "border-transparent bg-gradient-to-br from-blue-600 to-violet-600 shadow-sm shadow-blue-500/30"
                            : "border-slate-300 bg-white group-hover:border-blue-400"
                        }`}
                      >
                        <Check
                          className={`h-3.5 w-3.5 text-white transition-transform ${
                            acceptedPrivacy ? "scale-100" : "scale-0"
                          }`}
                          strokeWidth={3}
                        />
                      </span>
                      <span className="text-xs leading-relaxed text-slate-500">
                        Akceptuję{" "}
                        <a
                          href="/dokumenty/polityka-prywatnosci"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="font-semibold text-blue-600 underline decoration-blue-300 underline-offset-2 transition-colors hover:text-violet-600 hover:decoration-violet-400"
                        >
                          politykę prywatności
                        </a>{" "}
                        i&nbsp;wyrażam zgodę na przetwarzanie mojego adresu e-mail.
                      </span>
                    </label>

                    {error && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-rose-600 font-medium"
                      >
                        {error}
                      </motion.p>
                    )}
                  </motion.form>
                )}
              </AnimatePresence>
            </div>

          </motion.div>

          {/* ─── Wizualizacja po prawej: nauczyciele + podgląd lekcji + status przedmiotów ─── */}
          <div className="relative hidden lg:block">
            <div
              aria-hidden
              className="pointer-events-none absolute -top-12 -right-10 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-10 -left-10 h-64 w-64 rounded-full bg-violet-200/40 blur-3xl"
            />

            {heroTeachers.length > 0 && (
              <div className="absolute -top-5 left-8 z-20 inline-flex items-center gap-2.5 rounded-full bg-white py-2 pl-2 pr-4 shadow-lg ring-1 ring-slate-100">
                <div className="flex -space-x-3">
                  {heroTeachers.map(([name, photo]) =>
                    photo ? (
                      <img
                        key={name}
                        src={photo}
                        alt={name}
                        className="h-8 w-8 rounded-full border-2 border-white object-cover"
                      />
                    ) : (
                      <div
                        key={name}
                        className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-blue-600 to-violet-600 text-[10px] font-bold text-white"
                      >
                        {name.charAt(0)}
                      </div>
                    )
                  )}
                </div>
                <span className="text-xs font-bold text-slate-700">Uczą Cię najlepsi nauczyciele</span>
              </div>
            )}

            {heroCourse ? (
              <Link
                href={`/kurs/${heroCourse.slug}`}
                className="group relative block rounded-[28px] border border-slate-200 bg-white p-3 shadow-2xl shadow-blue-900/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-blue-900/20"
              >
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-white to-violet-50">
                  {heroCourse.thumbnail ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={heroCourse.thumbnail}
                      alt={heroCourse.title}
                      className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <GraduationCap className="h-20 w-20 text-blue-200" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform duration-300 group-hover:scale-110">
                      <PlayCircle className="h-8 w-8 text-blue-700" />
                    </span>
                  </div>
                  {heroCourse.price > 0 && (
                    <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-blue-700 px-3 py-1.5 text-xs font-bold text-white shadow-md">
                      od {Math.round(heroCourse.price)} zł
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3 px-2 py-3.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-900">{heroCourse.title}</p>
                    {heroCourse.rating > 0 && (
                      <span className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-slate-500">
                        <Star className="h-3 w-3 text-amber-400" fill="currentColor" /> {heroCourse.rating.toFixed(1)}
                      </span>
                    )}
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 text-sm font-bold text-blue-700 transition-transform group-hover:translate-x-0.5">
                    Zobacz kurs <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ) : (
              <div className="relative rounded-[28px] border border-slate-200 bg-white p-3 shadow-2xl shadow-blue-900/10">
                <div className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-white to-violet-50">
                  <GraduationCap className="h-20 w-20 text-blue-200" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {coursesEnabled && landingCourses.length > 0 && (
        <section id="kursy" className="py-12 sm:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                {coursesTitle}
              </h2>
              <p className="mt-3 text-lg leading-relaxed text-slate-600">{coursesSubtitle}</p>
            </div>

            <div
              className={
                landingCourses.length === 1
                  ? "mt-12 flex justify-center"
                  : "mt-12 grid gap-6 sm:grid-cols-2"
              }
            >
              {landingCourses.map((c) => {
                const tagline =
                  c.landingTagline?.trim() ||
                  c.description?.split(/(?<=[.!?])\s/)[0]?.trim() ||
                  "Darmowe lekcje już dostępne";
                const promo = c.originalPrice > 0 && c.originalPrice > c.price;

                // ── Pojedynczy kurs: duża karta sprzedażowa (zdjęcie + panel z ofertą) ──
                if (landingCourses.length === 1) {
                  const features = [
                    c.totalLessons > 0 ? `${c.totalLessons} lekcji wideo` : "Lekcje wideo krok po kroku",
                    c.taskCount > 0
                      ? `${c.taskCount} zadań i testów do rozwiązania`
                      : "Zadania i testy po każdym temacie",
                    "Dostęp do matury 2027",
                  ];
                  return (
                    <article key={c.id} className="w-full max-w-6xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
                      <div className="group flex flex-col lg:flex-row">
                        <Link
                          href={`/kurs/${c.slug}`}
                          aria-label={`Zobacz kurs ${c.title}`}
                          className="relative block aspect-[16/10] shrink-0 overflow-hidden lg:aspect-auto lg:min-h-[520px] lg:w-[44%]"
                          style={{ backgroundColor: `${c.cardColor}12` }}
                        >
                          {c.thumbnail ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={c.thumbnail}
                              alt={c.title}
                              className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-[1.03]"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-white/70 to-transparent">
                              <GraduationCap className="h-14 w-14" style={{ color: c.cardColor }} />
                            </div>
                          )}
                          <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold text-emerald-700 shadow-sm backdrop-blur">
                            <Sparkles className="h-3.5 w-3.5" /> {c.freeLessonCount} darmowych lekcji
                          </span>
                        </Link>

                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-5 p-7 sm:p-10">
                          {c.promoEndsAt ? (
                            <PromoCountdown endsAt={c.promoEndsAt} variant="compact" className="self-start" />
                          ) : (
                            c.price > 0 && (
                              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700 ring-1 ring-emerald-200">
                                <CheckCircle2 className="h-4 w-4" /> Kurs dostępny do kupienia
                              </span>
                            )
                          )}

                          <div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-500">
                              {c.totalLessons > 0 && <span>{c.totalLessons} lekcji</span>}
                              {c.taskCount > 0 && (
                                <>
                                  <span>·</span>
                                  <span>{c.taskCount} zadań</span>
                                </>
                              )}
                              {c.rating > 0 && (
                                <>
                                  <span>·</span>
                                  <span className="flex items-center gap-1">
                                    <Star className="h-3 w-3 text-amber-400" fill="currentColor" />
                                    {c.rating.toFixed(1)}
                                  </span>
                                </>
                              )}
                            </div>
                            <Link
                              href={`/kurs/${c.slug}`}
                              className="mt-1.5 block text-2xl font-extrabold leading-snug text-slate-900 hover:text-blue-700 sm:text-3xl"
                            >
                              {c.title}
                            </Link>
                            {c.authorName && (
                              <p className="mt-1.5 text-sm font-medium text-slate-500">Uczy {c.authorName}</p>
                            )}
                          </div>

                          <ul className="space-y-2.5 text-sm font-medium text-slate-700">
                            {features.map((f) => (
                              <li key={f} className="flex items-center gap-2.5">
                                <Check className="h-4 w-4 shrink-0 text-emerald-600" /> {f}
                              </li>
                            ))}
                            {c.registrationBonusLessonId && c.firstFreeLessonId && (
                              <li className="flex items-center gap-2.5">
                                <Check className="h-4 w-4 shrink-0 text-emerald-600" /> +1 lekcja bonusowa po rejestracji
                              </li>
                            )}
                          </ul>

                          {/* Dowód społeczny — awatary tylko osób z publicznym profilem, liczba zawsze realna */}
                          {c.studentCount > 0 && (
                            <div className="flex items-center gap-2">
                              {c.studentAvatars.length > 0 && (
                                <div className="flex -space-x-2">
                                  {c.studentAvatars.map((avatar, i) =>
                                    avatar.image ? (
                                      <img
                                        key={i}
                                        src={avatar.image}
                                        alt=""
                                        className="h-8 w-8 rounded-full border-2 border-white object-cover"
                                      />
                                    ) : (
                                      <div
                                        key={i}
                                        className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-blue-600 to-violet-600"
                                      >
                                        <span className="text-[10px] font-bold text-white">{avatar.initials}</span>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                              <span className="text-xs font-semibold text-slate-500">
                                +{c.studentCount.toLocaleString("pl-PL")} osób już się uczy
                              </span>
                            </div>
                          )}

                          <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-5">
                            {promo && (
                              <span className="text-base text-slate-400 line-through">
                                {Math.round(c.originalPrice)} zł
                              </span>
                            )}
                            <span className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                              {c.price > 0 ? `${Math.round(c.price)} zł` : "Za darmo"}
                            </span>
                            {c.priceLabel && (
                              <span className="text-sm font-semibold text-slate-500">{formatPriceLabel(c.priceLabel)}</span>
                            )}
                          </div>

                          <div className="flex flex-col gap-2.5">
                            {c.price > 0 ? (
                              <button
                                type="button"
                                onClick={() => handleBuyCourse(c)}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-700 px-6 py-4 text-base font-bold text-white shadow-lg shadow-blue-900/20 transition-colors hover:bg-blue-800"
                              >
                                <ShoppingCart className="h-5 w-5" />
                                {hasItem(c.id) ? "W koszyku" : `Kup teraz · ${Math.round(c.price)} zł`}
                              </button>
                            ) : (
                              <Link
                                href={`/kurs/${c.slug}/odbierz`}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-4 text-base font-bold text-white transition-colors hover:bg-emerald-700"
                              >
                                <Gift className="h-5 w-5" /> Odbierz darmowe lekcje
                              </Link>
                            )}

                            <div className="flex flex-col gap-2 sm:flex-row">
                              <Link
                                href={`/kurs/${c.slug}`}
                                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700 transition-colors hover:border-blue-200 hover:bg-blue-50"
                              >
                                Zobacz najpierw kurs <ArrowRight className="h-4 w-4" />
                              </Link>
                              {c.registrationBonusLessonId && c.firstFreeLessonId && (
                                <Link
                                  href={`/platforma/register?redirect=${encodeURIComponent(`/kurs/${c.slug}/odbierz?bonusLessonId=${c.registrationBonusLessonId}&previewLessonId=${c.firstFreeLessonId}&claimBonusOnly=1`)}`}
                                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 transition-colors hover:bg-emerald-100"
                                >
                                  <Gift className="h-4 w-4 shrink-0" /> Odbierz bonusową lekcję
                                </Link>
                              )}
                            </div>
                          </div>

                          <p className="text-center text-xs text-slate-500 sm:text-left">
                            Płatność kartą, BLIK lub Przelewy24.
                          </p>
                        </div>
                      </div>
                    </article>
                  );
                }

                // ── Kilka kursów obok siebie: lżejsza karta w siatce ──
                return (
                  <article
                    key={c.id}
                    className="w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
                  >
                    <div className="group flex h-full flex-col">
                      <Link
                        href={`/kurs/${c.slug}`}
                        aria-label={`Zobacz kurs ${c.title}`}
                        className="relative block aspect-[16/9] shrink-0 overflow-hidden"
                        style={{ backgroundColor: `${c.cardColor}12` }}
                      >
                        {c.thumbnail ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={c.thumbnail}
                            alt={c.title}
                            className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-[1.025]"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-white/70 to-transparent">
                            <GraduationCap className="h-12 w-12" style={{ color: c.cardColor }} />
                          </div>
                        )}
                        <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold text-emerald-700 shadow-sm backdrop-blur">
                          <Sparkles className="h-3.5 w-3.5" /> {c.freeLessonCount} darmowych lekcji
                        </span>
                      </Link>
                      <div className="flex min-w-0 flex-1 flex-col p-6">
                        {c.price > 0 && (
                          <span className="mb-3 inline-flex w-fit items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700 ring-1 ring-emerald-200">
                            <CheckCircle2 className="h-4 w-4" /> Kurs dostępny do kupienia
                          </span>
                        )}
                        <Link href={`/kurs/${c.slug}`} className="w-fit text-lg font-extrabold leading-snug text-slate-900 hover:text-blue-700">
                          {c.title}
                        </Link>
                        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-500">{tagline}</p>
                        {c.registrationBonusLessonId && c.firstFreeLessonId && (
                          <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                            <Gift className="h-4 w-4 shrink-0" /> +1 lekcja po rejestracji
                          </p>
                        )}
                        <div className="mt-auto flex flex-wrap items-end gap-2 pt-5">
                          {promo && (
                            <span className="text-sm text-slate-400 line-through">
                              {Math.round(c.originalPrice)} zł
                            </span>
                          )}
                          <span className="text-2xl font-extrabold text-slate-900">
                            {c.price > 0 ? `${Math.round(c.price)} zł` : "Za darmo"}
                          </span>
                          {c.priceLabel && (
                            <span className="text-sm font-semibold text-slate-500">{formatPriceLabel(c.priceLabel)}</span>
                          )}
                        </div>
                        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                          <Link
                            href={`/kurs/${c.slug}`}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700 transition-colors hover:border-blue-200 hover:bg-blue-50"
                          >
                            Zobacz kurs <ArrowRight className="h-4 w-4" />
                          </Link>
                          {c.registrationBonusLessonId && c.firstFreeLessonId && (
                            <Link
                              href={`/platforma/register?redirect=${encodeURIComponent(`/kurs/${c.slug}/odbierz?bonusLessonId=${c.registrationBonusLessonId}&previewLessonId=${c.firstFreeLessonId}&claimBonusOnly=1`)}`}
                              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-blue-800"
                            >
                              <Gift className="h-4 w-4 shrink-0" />
                              Zarejestruj się i odbierz 1 dodatkową lekcję
                            </Link>
                          )}
                        </div>
                        {c.registrationBonusLessonId && c.firstFreeLessonId && (
                          <p className="mt-2 text-xs leading-relaxed text-slate-500">
                            Po rejestracji odblokujesz lekcję bonusową i od razu przejdziesz do nauki.
                          </p>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {landingCourses.length > 1 && (
              <div className="mt-8 flex justify-center">
                <Link
                  href="/kursy-maturalne"
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-6 py-3 font-bold text-white transition-colors hover:bg-blue-800"
                >
                  {coursesCtaText} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── Kim jesteśmy ─── */}
      <section className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <span className="text-sm font-semibold text-blue-600 uppercase tracking-wider">
              Kim jesteśmy
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 mb-4">
              Najlepsi nauczyciele w jednym miejscu
            </h2>
            <p className="text-slate-600 leading-relaxed">
              NextSchool wspiera przygotowania do matury 2027, łącząc materiały edukacyjne,
              narzędzia do nauki i praktyczne wskazówki.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                icon: Users,
                title: "Zespół ekspertów",
                desc: "Doświadczeni nauczyciele wspierają Cię w przygotowaniach do egzaminu.",
              },
              {
                icon: BookOpen,
                title: "Sprawdzone metody",
                desc: "Materiały i zadania pomagają utrwalać kluczowe zagadnienia.",
              },
              {
                icon: Trophy,
                title: "Nauka we własnym tempie",
                desc: "Korzystaj z planu i materiałów, aby systematycznie przygotowywać się do matury.",
              },
            ].map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 bg-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 mb-4 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center shadow-md">
                  <card.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2">{card.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Jak to działa ─── */}
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <span className="text-sm font-semibold text-emerald-600 uppercase tracking-wider">
              Jak to działa
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 mb-4">
              Trzy kroki do gotowego planu
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Zero kombinowania. Wszystko gotowe, żebyś mógł zacząć jeszcze dzisiaj.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6 relative">
            {/* Connecting line on desktop */}
            <div
              aria-hidden
              className="hidden sm:block absolute top-10 left-[16.6%] right-[16.6%] h-px bg-gradient-to-r from-blue-200 via-violet-200 to-emerald-200"
            />
            {HOW_IT_WORKS.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative text-center"
              >
                <div className="relative inline-flex w-20 h-20 mb-5 rounded-2xl bg-white ring-1 ring-slate-200 shadow-md items-center justify-center">
                  <s.icon className="w-8 h-8 text-blue-600" strokeWidth={2} />
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-white text-xs font-extrabold flex items-center justify-center shadow-md">
                    {s.step}
                  </span>
                </div>
                <h3 className="font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Co dostaniesz w planie ─── */}
      <section id="features" className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <span className="text-sm font-semibold text-violet-600 uppercase tracking-wider">
              Co dostaniesz
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 mb-4">
              Wszystko, czego potrzebujesz do matury 2027
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Twój kompletny plan przygotowania w jednym miejscu. Bez gubienia się w internecie.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PLAN_FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="p-6 bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200/60 hover:border-blue-300 hover:shadow-lg transition-all group"
              >
                <div className="w-11 h-11 mb-4 rounded-xl bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
                  <feature.icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-bold mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Dlaczego NextSchool ─── */}
      <section className="relative py-20 sm:py-24 overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-10 left-[15%] w-72 h-72 rounded-full bg-blue-400/15 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -bottom-10 right-[12%] w-80 h-80 rounded-full bg-amber-400/15 blur-3xl"
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <span className="text-sm font-semibold text-blue-600 uppercase tracking-wider">
              Dlaczego my
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 mb-4">
              Stworzone, żeby dać Ci przewagę
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Nie zaczynaj matury z pustą kartką. Dostajesz gotowy plan, sprawdzone
              materiały i wsparcie na każdym etapie nauki.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_US.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, type: "spring", stiffness: 120, damping: 16 }}
                whileHover={{ y: -6 }}
                className="group relative p-6 bg-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div
                  className={`pointer-events-none absolute -inset-px -z-10 rounded-2xl ${item.glow} opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100`}
                />
                <div
                  className={`w-12 h-12 mb-4 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-md transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3`}
                >
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* pasek obietnicy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-medium text-slate-600"
          >
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Bez karty płatniczej
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Plan w 60 sekund
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Zgodne z maturą 2027
            </span>
          </motion.div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-blue-600 uppercase tracking-wider">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
              Często zadawane pytania
            </h2>
          </div>

          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-50/50 transition-colors"
                >
                  <span className="font-semibold text-slate-900">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="px-5 pb-5 text-sm text-slate-600 leading-relaxed"
                  >
                    {item.a}
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-violet-700 p-8 sm:p-14 text-center shadow-2xl">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                backgroundSize: "32px 32px",
              }}
            />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-4">
                Wybierz kurs maturalny 2027
              </h2>
              <p className="text-blue-100 text-lg max-w-xl mx-auto mb-8">
                Sprawdź program, darmowe lekcje i cenę kursu.
              </p>
              <a
                href="/kursy-maturalne"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-700 font-bold rounded-xl transition-colors hover:bg-blue-50"
              >
                Zobacz kursy i ceny <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Social media / społeczność ─── */}
      {socialEnabled && socials.length > 0 && (
        <section id="social" className="relative py-20 sm:py-24 overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-blue-50/40 to-white" />
          <div
            aria-hidden
            className="absolute top-10 right-[10%] w-72 h-72 rounded-full bg-fuchsia-400/10 blur-3xl"
          />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-2xl mx-auto mb-12"
            >
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 uppercase tracking-wider">
                <Users className="w-4 h-4" /> Social media
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 mb-4">
                {socialTitle}
              </h2>
              <p className="text-slate-600 leading-relaxed">{socialSubtitle}</p>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {socials.map((social, i) => {
                const meta = SOCIAL_META[social.platform];
                if (!meta) return null;
                const { Icon, label, gradient, glow } = meta;
                const external = social.href.startsWith("http");
                return (
                  <motion.a
                    key={social.id}
                    href={social.href || "#"}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    aria-label={label}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06, type: "spring", stiffness: 120, damping: 16 }}
                    whileHover={{ y: -6 }}
                    className="group relative flex flex-col items-center gap-3 p-6 bg-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-xl transition-shadow text-center"
                  >
                    <div
                      className={`pointer-events-none absolute -inset-px -z-10 rounded-2xl ${glow} opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100`}
                    />
                    <div
                      className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3`}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">{label}</p>
                      {social.label && (
                        <p className="text-xs text-slate-500 mt-0.5">{social.label}</p>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
                      Obserwuj <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </motion.a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ─── Program ambasadorski ─── */}
      {ambassadorEnabled && (
        <section id="ambasador" className="py-16 sm:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white shadow-2xl"
            >
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-30"
                style={{
                  background:
                    "radial-gradient(600px 300px at 20% 15%, rgba(255,255,255,0.28), transparent 60%)",
                }}
              />
              <div className="relative grid md:grid-cols-2 gap-8 lg:gap-10 items-center p-8 sm:p-12">
                <div>
                  <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/15 ring-1 ring-white/25 text-sm font-semibold backdrop-blur-sm">
                    <span className="text-base">💰</span> Program ambasadorski
                  </span>
                  <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
                    Zarabiaj, polecając NextSchool
                  </h2>
                  <p className="mt-4 text-white/90 text-base sm:text-lg leading-relaxed max-w-xl">
                    Uczysz się do matury i tak polecasz znajomym rzeczy, które działają? Zostań
                    ambasadorem i odbieraj prowizję za każdą sprzedaż — bez wpłat, bez ryzyka,
                    we własnym tempie.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-white/90">
                    {["Bez wpłat", "Bez ryzyka", "Gotowe materiały co tydzień"].map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-white" /> {t}
                      </span>
                    ))}
                  </div>
                  <a
                    href="/ambasador"
                    className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-violet-700 font-bold shadow-lg hover:-translate-y-0.5 transition-all"
                  >
                    Zostań ambasadorem <ArrowRight className="w-5 h-5" />
                  </a>
                </div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15, duration: 0.5 }}
                  className="relative rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur-md p-6 shadow-xl"
                >
                  <div className="text-xs uppercase tracking-wider text-white/70 font-semibold">
                    Przykład rozliczenia
                  </div>
                  <div className="mt-1 text-4xl font-extrabold">1 250 zł</div>
                  <div className="mt-5 space-y-3">
                    {[
                      { emoji: "📈", label: "Sprzedanych kursów", value: "18" },
                      { emoji: "🔗", label: "Aktualna stawka", value: "75 zł / kurs" },
                      { emoji: "🏆", label: "Limit programu", value: "3 000 zł" },
                    ].map((r) => (
                      <div
                        key={r.label}
                        className="flex items-center justify-between gap-3 rounded-xl bg-white/10 px-4 py-3"
                      >
                        <span className="inline-flex items-center gap-2 text-sm text-white/90">
                          <span className="text-lg">{r.emoji}</span> {r.label}
                        </span>
                        <span className="text-sm font-bold">{r.value}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ─── Karuzela wpisów z bloga (opcjonalna) ─── */}
      {blogCarouselEnabled && <BlogNewsCarousel />}

      {/* ─── Footer (stopka sterowana z CMS — zakres „Matura 2027") ─── */}
      <Footer scope="matura2027" />

    </div>
    </MotionConfig>
    </>
  );
}
